import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Shield, Clock, Award, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import aboutImage from "@/assets/about-fischer.webp";

const stats = [
  { icon: Shield, value: "Licensed", label: "& Insured" },
  { icon: Clock, value: "24/7", label: "Availability" },
  { icon: Award, value: "5.0★", label: "Rated Service" },
  { icon: Wrench, value: "100%", label: "Satisfaction" },
];

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-24 bg-gradient-dark relative overflow-hidden noise-overlay">
      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-elevated">
              <img
                src={aboutImage}
                alt="Fischer McKenzie - Owner of Onyx Professional Plumbing in Gulf Shores Alabama providing expert plumbing services"
                className="w-full aspect-[4/3] md:aspect-[4/4] lg:aspect-[4/5] object-cover min-h-[280px] sm:min-h-[320px] md:min-h-[400px]"
                width="600"
                height="750"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent" />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="text-primary font-semibold tracking-wider uppercase text-sm">
              About Onyx
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6 leading-tight">
              Built on Integrity & Craftsmanship
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              Led by owner Fischer McKenzie Onyx Professional Plumbing is based in Gulf Shores and serves Foley, Orange Beach, Fairhope, Daphne, Spanish Fort, Ono Island, and all of Baldwin County. With Alabama License #MP-4174, we're committed to doing the job right the first time.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              Fischer and his skilled team bring integrity, reliability, and craftsmanship to
              every project—from emergency plumbing repairs and water heater repair in Baldwin County to drain cleaning in Gulf Shores, AL and complete kitchen and bathroom remodels. We treat each home or business as if it were our own.
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-3 p-4 rounded-xl bg-muted/30 border border-border/50"
                >
                  <stat.icon className="h-8 w-8 text-primary" />
                  <div>
                    <div className="font-bold text-foreground">{stat.value}</div>
                    <div className="text-xs text-muted-foreground">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <Button variant="outlineBrand" asChild>
              <Link to="/about">Learn More About Us</Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
